import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Mail,
  Send,
  Inbox,
  AlertCircle,
  CheckCircle,
  Clock,
  Paperclip,
  Archive,
  Trash2,
  Reply,
  AlertTriangle,
} from 'lucide-react';

// Message types
interface Message {
  id: string;
  subject: string;
  message: string;
  isUrgent: boolean;
  status: 'unread' | 'read' | 'replied';
  sentAt: string;
  repliedAt?: string;
  replyMessage?: string;
  attachments?: string[];
}

const mockMessages: Message[] = [
  {
    id: 'msg-1',
    subject: 'Question about Group Buying feature',
    message:
      'Hi team! I have a neighbor asking if the group buying feature will work with their existing lawn service contract. Can you clarify how it works with pre-existing contracts?',
    isUrgent: false,
    status: 'replied',
    sentAt: '2025-10-28T14:30:00Z',
    repliedAt: '2025-10-28T16:45:00Z',
    replyMessage:
      'Great question! Group buying works alongside existing contracts. Neighbors can opt-in when their current contract expires, or we can negotiate with their existing vendor for group rates. We have a guide coming out next week!',
  },
  {
    id: 'msg-2',
    subject: 'Demo link not working',
    message:
      "One of my neighbors tried the voting demo link and got a 404 error. Can you check if there's an issue? They were really excited to see it.",
    isUrgent: true,
    status: 'replied',
    sentAt: '2025-10-27T10:15:00Z',
    repliedAt: '2025-10-27T11:20:00Z',
    replyMessage:
      "Thanks for reporting this! We had a temporary server issue but it's fixed now. The demo should work perfectly. Please have them try again and let us know if there are any other issues.",
  },
  {
    id: 'msg-3',
    subject: 'Feedback from neighborhood meeting',
    message:
      'I presented the platform at our HOA meeting last night. Collected 15+ feedback forms! Most people are excited about the voting system. A few have concerns about data privacy - do you have any materials I can share about security?',
    isUrgent: false,
    status: 'read',
    sentAt: '2025-10-26T09:20:00Z',
  },
  {
    id: 'msg-4',
    subject: 'Suggestion: Translation features',
    message:
      'We have a large Spanish-speaking community in Lake Charles. Would it be possible to add Spanish language support? This could really help adoption.',
    isUrgent: false,
    status: 'unread',
    sentAt: '2025-10-25T14:00:00Z',
  },
];

const statusColors = {
  unread: 'bg-blue-100 text-blue-800 border-blue-200',
  read: 'bg-gray-100 text-gray-800 border-gray-200',
  replied: 'bg-green-100 text-green-800 border-green-200',
};

const statusLabels = {
  unread: 'Unread',
  read: 'Read',
  replied: 'Replied',
};

export function DirectMessaging() {
  const [activeTab, setActiveTab] = useState<'compose' | 'inbox'>('compose');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);

  // Handle message submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!subject.trim()) {
      setError('Please enter a subject');
      return;
    }
    if (!message.trim() || message.trim().split(/\s+/).length < 5) {
      setError('Please provide a detailed message (at least 5 words)');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Submit to backend
      // const response = await sendChampionMessage({ subject, message, isUrgent });

      // Mock submission
      await new Promise((resolve) => setTimeout(resolve, 800));

      console.log('Message sent:', { subject, message, isUrgent });

      setSubmitSuccess(true);
      setSubject('');
      setMessage('');
      setIsUrgent(false);

      setTimeout(() => {
        setSubmitSuccess(false);
      }, 3000);
    } catch (err) {
      setError('Failed to send message. Please try again.');
      console.error('Send error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate stats
  const unreadCount = mockMessages.filter((m) => m.status === 'unread').length;
  const repliedCount = mockMessages.filter((m) => m.status === 'replied').length;
  const urgentCount = mockMessages.filter((m) => m.isUrgent && m.status !== 'replied').length;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Messages</h1>
        <p className="text-muted-foreground">
          Contact the ToolboxLogic team directly. We typically respond within 24 hours.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{mockMessages.length}</p>
                <p className="text-sm text-muted-foreground">Total Messages</p>
              </div>
              <Mail className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{unreadCount}</p>
                <p className="text-sm text-muted-foreground">Unread</p>
              </div>
              <Inbox className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{repliedCount}</p>
                <p className="text-sm text-muted-foreground">Replied</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tab Navigation */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Button
              variant={activeTab === 'compose' ? 'default' : 'outline'}
              onClick={() => setActiveTab('compose')}
              className="flex-1"
            >
              <Send className="h-4 w-4 mr-2" />
              Compose Message
            </Button>
            <Button
              variant={activeTab === 'inbox' ? 'default' : 'outline'}
              onClick={() => setActiveTab('inbox')}
              className="flex-1"
            >
              <Inbox className="h-4 w-4 mr-2" />
              Inbox
              {unreadCount > 0 && (
                <Badge variant="destructive" className="ml-2">
                  {unreadCount}
                </Badge>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Compose Message Tab */}
      {activeTab === 'compose' && (
        <Card>
          <CardHeader>
            <CardTitle>Send a Message to ToolboxLogic</CardTitle>
            <CardDescription>
              Questions, feedback, or urgent issues? We're here to help!
            </CardDescription>
          </CardHeader>
          <CardContent>
            {submitSuccess ? (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Preview only. Messaging is not connected yet, so your message was not sent and no
                  one will receive it.
                </AlertDescription>
              </Alert>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Brief summary of your message"
                    maxLength={100}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Provide details about your question, feedback, or issue (at least 5 words)"
                    rows={8}
                  />
                  <p className="text-xs text-muted-foreground">
                    {
                      message
                        .trim()
                        .split(/\s+/)
                        .filter((w) => w).length
                    }{' '}
                    words (minimum 5)
                  </p>
                </div>

                {/* Urgent Flag */}
                <div className="flex items-center gap-2 p-4 border rounded-lg">
                  <input
                    type="checkbox"
                    id="urgent"
                    checked={isUrgent}
                    onChange={(e) => setIsUrgent(e.target.checked)}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="urgent" className="cursor-pointer flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <span>This is urgent (demo broken, blocker issue, etc.)</span>
                  </Label>
                </div>

                {isUrgent && (
                  <Alert>
                    <Clock className="h-4 w-4" />
                    <AlertDescription>
                      Urgent messages are prioritized and typically receive a response within 4-6
                      hours during business hours.
                    </AlertDescription>
                  </Alert>
                )}

                <Button type="submit" disabled={isSubmitting} className="w-full">
                  {isSubmitting ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      )}

      {/* Inbox Tab */}
      {activeTab === 'inbox' && (
        <Card>
          <CardHeader>
            <CardTitle>Your Messages</CardTitle>
            <CardDescription>All messages you've sent to the ToolboxLogic team</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {mockMessages.length === 0 ? (
                <div className="text-center py-12">
                  <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No messages yet</p>
                </div>
              ) : (
                mockMessages.map((msg) => (
                  <Card
                    key={msg.id}
                    className={`hover:shadow-md transition-shadow cursor-pointer ${
                      msg.status === 'unread' ? 'border-l-4 border-l-blue-500' : ''
                    }`}
                    onClick={() => setSelectedMessage(msg)}
                  >
                    <CardContent className="pt-6">
                      <div className="space-y-2">
                        {/* Header */}
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold truncate">{msg.subject}</h3>
                              {msg.isUrgent && (
                                <Badge variant="destructive" className="text-xs">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Urgent
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {msg.message}
                            </p>
                          </div>
                          <Badge className={statusColors[msg.status]}>
                            {statusLabels[msg.status]}
                          </Badge>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
                          <div className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            <span>
                              Sent{' '}
                              {new Date(msg.sentAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                          {msg.status === 'replied' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-3 w-3" />
                              <span>Replied</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedMessage(null)}
        >
          <Card
            className="max-w-3xl w-full max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <CardTitle>{selectedMessage.subject}</CardTitle>
                    {selectedMessage.isUrgent && (
                      <Badge variant="destructive">
                        <AlertTriangle className="h-3 w-3 mr-1" />
                        Urgent
                      </Badge>
                    )}
                    <Badge className={statusColors[selectedMessage.status]}>
                      {statusLabels[selectedMessage.status]}
                    </Badge>
                  </div>
                  <CardDescription className="flex items-center gap-1">
                    <Clock className="h-4 w-4" />
                    Sent{' '}
                    {new Date(selectedMessage.sentAt).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    })}
                  </CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelectedMessage(null)}>
                  ✕
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Original Message */}
              <div>
                <h4 className="font-semibold mb-2">Your Message:</h4>
                <div className="bg-muted/50 rounded-lg p-4 whitespace-pre-line">
                  {selectedMessage.message}
                </div>
              </div>

              {/* Reply */}
              {selectedMessage.status === 'replied' && selectedMessage.replyMessage && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Reply className="h-4 w-4 text-green-600" />
                    <h4 className="font-semibold text-green-600">ToolboxLogic Team Reply:</h4>
                    <span className="text-xs text-muted-foreground">
                      {new Date(selectedMessage.repliedAt!).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 whitespace-pre-line">
                    {selectedMessage.replyMessage}
                  </div>
                </div>
              )}

              {/* Pending Reply */}
              {selectedMessage.status !== 'replied' && (
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>
                    {selectedMessage.isUrgent
                      ? 'Our team is working on your urgent message. You should receive a reply within 4-6 hours during business hours.'
                      : 'We typically respond to messages within 24 hours. Thank you for your patience!'}
                  </AlertDescription>
                </Alert>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={() => setSelectedMessage(null)}
                  className="flex-1"
                >
                  Close
                </Button>
                {selectedMessage.status === 'replied' && (
                  <Button
                    onClick={() => {
                      setSelectedMessage(null);
                      setActiveTab('compose');
                      setSubject(`Re: ${selectedMessage.subject}`);
                    }}
                    className="flex-1"
                  >
                    <Reply className="h-4 w-4 mr-2" />
                    Reply
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
